import { createFileRoute } from "@tanstack/react-router";
import { AddressFields } from "~/components/address-fields";
import { Field } from "~/components/ui/field";
import { MoneyInput } from "~/components/ui/money-input";
import { Panel } from "~/components/ui/page";
import { CompanySettingsForm } from "~/features/settings/company-form";
import { DeliveryAddressesSettings } from "~/features/settings/delivery-addresses";
import { LogoField } from "~/features/settings/logo-field";
import { SettingsIntro, SettingsSection } from "~/features/settings/section";
import { useT } from "~/lib/i18n";

export const Route = createFileRoute("/_app/settings/company")({ component: CompanyIdentityPage });

/** Who the company is: printed on every document and in the Factur-X. */
function CompanyIdentityPage() {
  const t = useT();
  const optional = t("common.states.optional");
  return (
    <>
      <SettingsIntro text={t("settings.pages.company.subtitle")} />
      <CompanySettingsForm>
        {({ company, draft, editable, errors, set, err, text }) => (
          <>
            <Panel>
              <SettingsSection title={t("company.identity")}>
                <div className="grid gap-4 sm:grid-cols-2">
                  {text("legalName", t("company.legalName"))}
                  {text("tradeName", t("company.tradeName"))}
                  {text("legalForm", t("company.legalForm"), { optional: true })}
                  <Field label={t("company.capital")} optionalLabel={optional} error={err("capitalCents")}>
                    <MoneyInput value={draft.capitalCents} onChange={set("capitalCents")} suffix="€" disabled={!editable} />
                  </Field>
                  {text("siren", t("company.siren"), { optional: true, inputMode: "numeric" })}
                  {text("rcs", t("company.rcs"), { optional: true })}
                  {text("vatNumber", t("company.vatNumber"), { optional: true })}
                  {text("eori", t("company.eori"), { optional: true, hint: t("company.eoriHint") })}
                </div>
              </SettingsSection>
              <SettingsSection title={t("company.contact")}>
                <div className="flex flex-col gap-4">
                  <AddressFields value={draft.address} onChange={set("address")} errors={errors} prefix="address" />
                  <div className="grid gap-4 sm:grid-cols-2">
                    {text("email", t("common.fields.email"), { optional: true, inputMode: "email", type: "email" })}
                    {text("phone", t("common.fields.phone"), { optional: true, inputMode: "tel" })}
                    {text("website", t("common.fields.website"), { optional: true, inputMode: "url" })}
                  </div>
                </div>
              </SettingsSection>
              <SettingsSection title={t("company.customs")} description={t("company.customsHint")}>
                <div className="grid gap-4 sm:grid-cols-2">
                  {text("customsOriginCountry", t("company.customsOriginCountry"), { optional: true })}
                  {text("customsHsCode", t("company.customsHsCode"), { optional: true, inputMode: "numeric" })}
                </div>
              </SettingsSection>
              <SettingsSection title={t("company.bank")} description={t("company.bankHint")}>
                <div className="grid gap-4 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                  {text("iban", t("company.iban"), { optional: true })}
                  {text("bic", t("company.bic"), { optional: true })}
                </div>
              </SettingsSection>
            </Panel>
            <Panel>
              <SettingsSection title={t("company.documents")} description={t("company.logoHint")}>
                <LogoField company={company} editable={editable} />
              </SettingsSection>
            </Panel>
          </>
        )}
      </CompanySettingsForm>
      {/* Saved on its own: purchase orders pick from these; the legal address above stays the billing one. */}
      <Panel>
        <SettingsSection title={t("company.deliveryAddresses.title")} description={t("company.deliveryAddresses.hint")}>
          <DeliveryAddressesSettings />
        </SettingsSection>
      </Panel>
    </>
  );
}
